import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import {
  AuthenticationSteps,
  AuthState,
  defaultOrganisationFeatures,
  defaultSelectedOrganistionUserFeatures,
  User,
  UserFeatures,
  UserOrganisation,
} from 'store/auth/types';
import { State } from 'store/types';
import { createSelectorCached, UID, UUID } from 'types/types';

type AuthStateTree = Omit<Partial<State>, 'auth'> & Pick<State, 'auth'>;

const selectAuth = (state: AuthStateTree): AuthState => state.auth;

export const selectAuthSignInAs = createSelector(
  selectAuth,
  (auth): string | undefined => auth.authStepAs,
);

export const selectAuthStep = createSelector(
  selectAuth,
  (auth: AuthState): AuthenticationSteps => auth.step,
);

export const selectAuthStepToken = createSelector(
  selectAuth,
  (auth: AuthState): string | undefined => auth.authStepToken,
);

export const selectAuthStepAs = createSelector(
  selectAuth,
  (auth): boolean => !!auth?.authStepCanAs,
);

export const selectAuthError = createSelector(
  selectAuth,
  (auth: AuthState): string | undefined => auth.error,
);

export const selectAuthToken = createSelector(
  selectAuth,
  (auth: AuthState): string | undefined => auth.authorization_token,
);

export const selectHasAuthToken = createSelector(
  selectAuthToken,
  (token: string | undefined): boolean => !!token,
);

export const selectUrlToken = createSelector(
  selectAuth,
  (auth: AuthState) => auth.urlToken,
);

export const selectUser = createSelector(
  selectAuth,
  (auth: AuthState): User | undefined => auth.user,
);

export const selectUserTimestamp = createSelector(
  selectUser,
  (user: User | undefined): number => user?.timestamp || 0,
);

export const selectUserReference = createSelector(
  selectUser,
  (user: User | undefined): string | undefined =>
    // v6.34 "user.id" changed to "user.reference". The new "user.id" is the user's uid
    user?.reference || user?.id,
);

export const selectUserId = createSelector(
  selectUser,
  (user: User | undefined): string | undefined =>
    user?.reference ? user?.id : undefined,
);

export const selectUserName = createSelector(
  selectUser,
  (user: User | undefined): string | undefined => user?.name,
);

export const selectUserEmail = createSelector(
  selectUser,
  (user: User | undefined): string | undefined => user?.email,
);

export const selectUserTfaRequired = createSelector(
  selectUser,
  (user): boolean => !!user?.tfaRequired,
);

export const selectUserTfaEnabled = createSelector(
  selectUser,
  (user): boolean => !!user?.tfa,
);

export const selectUserEmailWithAuthStepEmail = createSelector(
  selectAuth,
  (auth): string | undefined => auth.authStepEmail || auth.user?.email,
);

export const selectUserAuthStepTfaSetup = createSelector(
  selectAuth,
  (auth) => auth.authStepTfaSetup,
);

export const selectUserAvatar = createSelector(
  selectUser,
  (user: User | undefined): string | null | undefined => user?.avatar,
);

export const selectUserIsSupport = createSelector(
  selectUser,
  (user): boolean => !!user?.isSupport,
);

const selectUserFeatures = createSelector(
  selectUser,
  (user: User | undefined): UserFeatures =>
    user?.features || { createAsset: false },
);

export const selectUserFeatureCreateAsset = createSelector(
  selectUserFeatures,
  (features: UserFeatures): boolean => features.createAsset || false,
);

export const selectPromptAuth = createSelector(
  selectAuth,
  (auth: AuthState): boolean => !!auth.promptAuth,
);

const selectUserOrganisations = createSelector(
  selectAuth,
  (auth: AuthState) => auth.organisations,
);

export const selectUserSelectedOrganisation = createSelector(
  selectAuth,
  (auth) => auth.organisation,
);

const selectUserSelectedOrganisationFeatures = createSelector(
  selectAuth,
  (auth) =>
    auth.organisation?.userFeatures || defaultSelectedOrganistionUserFeatures,
);

export const selectUserSelectedOrganisationFeature = createSelectorCached(
  [
    selectUserSelectedOrganisationFeatures,
    (_: State, feature: keyof typeof defaultSelectedOrganistionUserFeatures) =>
      feature,
  ],
  (features, feature) => features[feature],
);

export const selectUserSelectedOrganisationId = createSelector(
  selectUserSelectedOrganisation,
  (organisation) => organisation?.id,
);

const selectUserOrganisationUuidsToUids = createSelector(
  selectUserOrganisations,
  (_: State, organisationId: UID) => organisationId,
  (organisations): Record<UUID, UID> =>
    Object.values(organisations).reduce<Record<UUID, UID>>((acc, org) => {
      acc[org.uuid] = org.id;
      return acc;
    }, {}),
);

export const selectUserOrganisationUuidToUid = createCachedSelector(
  selectUserOrganisationUuidsToUids,
  (_: State, organisationId: UID) => organisationId,
  (organisationUuidsToUids, organisationId): UID =>
    organisationUuidsToUids[organisationId] || '-1',
)((_: State, organisationId: UUID) => organisationId);

const selectUserOrganisation = createCachedSelector(
  selectUserOrganisations,
  (_: State, organisationId: UID) => organisationId,
  (organisations, organisationId) => organisations[organisationId],
)((_: State, organisationId: UID) => organisationId);

export const selectUserOrganisationFeatures = createCachedSelector(
  selectUserOrganisation,
  (organisation): Required<UserOrganisation['features']> => ({
    ...defaultOrganisationFeatures,
    ...(organisation?.features || {}),
  }),
)((_: State, organisationId: UID) => organisationId);

export const selectUserFeatureCanViewAssessmentsTab = createSelector(
  selectUserOrganisations,
  (organisations): boolean => {
    return !!Object.values(organisations).find(
      (org) => !!org.features.assessmentqa,
    );
  },
);

export const selectUserFeatureCanViewReportingTab = createSelector(
  selectUserOrganisations,
  (organisations): boolean => {
    return !!Object.values(organisations).find(
      (org) => !!org.features.reporting,
    );
  },
);
